interface _WaxEndpoints {
  rpc: string;
  hyperion: string;
  atomicApi: string;
  rpcBackup?: string;
  atomicBackup?: string;
}

export interface WaxConfig {
  network: 'mainnet' | 'testnet';
  rpcEndpoint: string;
  atomicApi: string;
  chainId: string;
  waxCloudWalletUrl: string; // WAX Cloud Wallet URL for WaxJS
  wcwApiUrl: string; // WAX Cloud Wallet API URL for account creation
  wcwApiKey: string; // WAX Cloud Wallet API key
  accountCreationFee: string; // in WAX tokens
  treasuryAccount?: string; // Treasury account name for minting operations
  treasuryPrivateKey?: string; // Treasury account private key (should not be exposed to client)
}

export interface TreasuryConfig {
  treasuryAccount: string;
  treasuryPrivateKey: string;
  network: 'mainnet' | 'testnet';
  rpcEndpoint: string;
  chainId: string;
}

const configs: Record<string, WaxConfig> = {
  mainnet: {
    network: 'mainnet',
    rpcEndpoint: process.env.WAX_MAINNET_RPC || 'https://wax.greymass.com',
    atomicApi: process.env.WAX_MAINNET_ATOMIC_API || 'https://aa.wax.blacklusion.io',
    chainId: '1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4',
    // Base URL only - Wharfkit plugin handles login/registration flow internally
    waxCloudWalletUrl: 'https://www.mycloudwallet.com',
    wcwApiUrl: process.env.WAX_CLOUD_WALLET_API_URL || 'https://api.mycloudwallet.com/v1/accounts',
    wcwApiKey: process.env.WAX_CLOUD_WALLET_API_KEY || '',
    accountCreationFee: '5.00000000 WAX',
    treasuryAccount: process.env.WAX_TREASURY_ACCOUNT,
    treasuryPrivateKey: process.env.WAX_TREASURY_PRIVATE_KEY,
  },
  testnet: {
    network: 'testnet',
    rpcEndpoint: process.env.WAX_TESTNET_RPC || 'https://wax-testnet.eosphere.io',
    atomicApi: process.env.WAX_TESTNET_ATOMIC_API || 'https://test-wax.api.atomicassets.io',
    chainId: 'f16b1833c747c43682f4386fca9cbb327929334a762755ebec17f6f23c9b8a12',
    // Always use mainnet Cloud Wallet UI - testnet version doesn't exist
    // Base URL only - Wharfkit plugin handles login/registration flow internally
    waxCloudWalletUrl: 'https://www.mycloudwallet.com',
    wcwApiUrl: process.env.WAX_CLOUD_WALLET_API_URL || 'https://api-testnet.mycloudwallet.com/v1/accounts',
    wcwApiKey: process.env.WAX_CLOUD_WALLET_API_KEY || '',
    accountCreationFee: '5.00000000 WAX',
    treasuryAccount: process.env.WAX_TESTNET_TREASURY_ACCOUNT || process.env.WAX_TREASURY_ACCOUNT,
    treasuryPrivateKey: process.env.WAX_TESTNET_PRIVATE_KEY || process.env.WAX_TREASURY_PRIVATE_KEY,
  }
};

export function getWaxConfig(): WaxConfig {
  // Use NEXT_PUBLIC_ prefix for client-side access, fallback to server-side WAX_NETWORK
  const network = (process.env.NEXT_PUBLIC_WAX_NETWORK || process.env.WAX_NETWORK || 'mainnet') as 'testnet' | 'mainnet';
  return configs[network];
}

/**
 * Get treasury configuration for blockchain transactions
 * This should only be used server-side for signing transactions
 * @throws Error if treasury account or private key not configured
 */
export function getTreasuryConfig(): TreasuryConfig {
  const waxConfig = getWaxConfig();

  if (!waxConfig.treasuryAccount) {
    throw new Error('WAX_TREASURY_ACCOUNT not configured. Set environment variable.');
  }

  if (!waxConfig.treasuryPrivateKey) {
    throw new Error('WAX_TREASURY_PRIVATE_KEY not configured. Set environment variable.');
  }

  return {
    treasuryAccount: waxConfig.treasuryAccount,
    treasuryPrivateKey: waxConfig.treasuryPrivateKey,
    network: waxConfig.network,
    rpcEndpoint: waxConfig.rpcEndpoint,
    chainId: waxConfig.chainId,
  };
}

/**
 * Check if treasury account is properly configured
 * Safe to call without throwing
 */
export function isTreasuryConfigured(): boolean {
  const waxConfig = getWaxConfig();
  return !!(waxConfig.treasuryAccount && waxConfig.treasuryPrivateKey);
}

export const waxConfig = getWaxConfig();

/**
 * Get WAX block explorer URL based on network
 * Mainnet: waxblock.io
 * Testnet: testnet.waxblock.io
 */
export function getWaxExplorerBaseUrl(): string {
  const config = getWaxConfig();
  return config.network === 'testnet'
    ? 'https://testnet.waxblock.io'
    : 'https://waxblock.io';
}

/**
 * Get full WAX transaction explorer URL
 * @param transactionId The blockchain transaction ID
 */
export function getWaxTransactionUrl(transactionId: string): string {
  return `${getWaxExplorerBaseUrl()}/transaction/${transactionId}`;
}

/**
 * Get full WAX account explorer URL
 * @param accountName The WAX account name
 */
export function getWaxAccountUrl(accountName: string): string {
  return `${getWaxExplorerBaseUrl()}/account/${accountName}`;
}
